import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import type { DimensionValue } from './dimensions';
import { ROSTER } from './tokens';

type Props = {
  label: string;
  options: readonly DimensionValue[];
  selected: readonly string[];
  onApply: (values: string[]) => void;
  onCancel: () => void;
  onBack: () => void;
};

export function CategoricalValuePicker({
  label,
  options,
  selected,
  onApply,
  onCancel,
  onBack,
}: Props) {
  const [checked, setChecked] = useState<string[]>([...selected]);
  const [query, setQuery] = useState('');
  const ordered = useMemo(() => {
    const wasSelected = new Set(selected);
    return [...options].sort(
      (a, b) => Number(wasSelected.has(b.value)) - Number(wasSelected.has(a.value)),
    );
  }, [options, selected]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q === '' ? ordered : ordered.filter((o) => o.label.toLowerCase().includes(q));
  }, [ordered, query]);

  const toggle = (value: string) =>
    setChecked((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );

  return (
    <Box data-testid="value-picker">
      <Box
        component="button"
        type="button"
        data-testid="value-picker-back"
        aria-label={`Back to filter list from ${label}`}
        onClick={onBack}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          p: '11px 14px',
          border: 0,
          borderBottom: `1px solid ${ROSTER.hairline}`,
          background: 'transparent',
          fontFamily: ROSTER.font,
          fontSize: 13,
          fontWeight: 700,
          color: (theme) => theme.palette.primary.main,
          cursor: 'pointer',
        }}
      >
        {`‹ ${label}`}
      </Box>
      <Box sx={{ p: '14px' }}>
        <Box
          sx={{
            fontFamily: ROSTER.font,
            fontSize: 12,
            fontWeight: 700,
            color: ROSTER.muted,
            mb: '8px',
          }}
        >
          Select one or more
        </Box>
        <Box
          component="input"
          data-testid="value-picker-search"
          aria-label={`Search ${label} values`}
          placeholder="Search values…"
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          sx={{
            width: '100%',
            mb: '8px',
            p: '6px 8px',
            border: `1px solid ${ROSTER.hairline}`,
            borderRadius: '6px',
            fontFamily: ROSTER.font,
            fontSize: 14,
            color: ROSTER.ink,
            '&::placeholder': { color: ROSTER.muted },
            '&:focus-visible': { outline: `2px solid ${ROSTER.ink}`, outlineOffset: '1px' },
          }}
        />
        <Box
          role="group"
          aria-label={`${label} values`}
          sx={{ maxHeight: 240, overflowY: 'auto' }}
        >
          {visible.length === 0 && (
            <Box
              role="status"
              sx={{ fontFamily: ROSTER.font, fontSize: 14, color: ROSTER.muted, py: '8px' }}
            >
              No values match that search.
            </Box>
          )}
          {visible.map((option) => (
            <Box
              key={option.value}
              component="label"
              data-testid="value-option"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                p: '7px 4px',
                fontFamily: ROSTER.font,
                fontSize: 14.5,
                color: ROSTER.ink,
                cursor: 'pointer',
              }}
            >
              <Box
                component="input"
                type="checkbox"
                checked={checked.includes(option.value)}
                onChange={() => toggle(option.value)}
                sx={{ width: 16, height: 16, accentColor: (theme) => theme.palette.primary.main }}
              />
              {option.label}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: '12px' }}>
          <Box
            component="button"
            type="button"
            data-testid="value-picker-cancel"
            onClick={onCancel}
            sx={{
              p: '7px 13px',
              border: 0,
              background: 'transparent',
              borderRadius: '6px',
              fontFamily: ROSTER.font,
              fontSize: 12,
              fontWeight: 700,
              color: ROSTER.muted,
              cursor: 'pointer',
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            type="button"
            data-testid="value-picker-apply"
            disabled={checked.length === 0}
            onClick={() => onApply(checked)}
            sx={{
              p: '7px 13px',
              border: 0,
              borderRadius: '6px',
              fontFamily: ROSTER.font,
              fontSize: 12,
              fontWeight: 700,
              color: '#FFFFFF',
              bgcolor: (theme) => theme.palette.primary.main,
              cursor: 'pointer',
              '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
            }}
          >
            Apply
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
